/**
 * CustomerMasterRejectionBanner
 *
 * Shown at the top of the customer's Order Files page when one or more
 * master files have been rejected by an admin and need a replacement.
 *
 *   • Lists each rejected file with admin's reason and the disc/layer it's on.
 *   • Per-file "Upload replacement" action: lets the customer pick a new file,
 *     which is uploaded with the SAME category as the rejected file via the
 *     existing chunked upload pipeline, then linked via `markMasterReplaced`
 *     so the old file is marked superseded.
 *   • Non-blocking: regular master upload card stays visible below.
 */
import { useMemo, useState } from 'react';
import { XCircle, Upload } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { ChunkedUploader } from './ChunkedUploader';
import { ordersApi } from '../lib/api';
import { toast } from 'sonner';

/**
 * Pretty per-file disc/layer label, derived from the order's master_slots.
 * Mirrors the labels used in the admin disc-grouped view so the customer
 * sees the same wording the admin used.
 */
const labelForFile = (file, order) => {
    const cat = file?.category || '';
    if (cat === 'audio_side_a') return 'Side A';
    if (cat === 'audio_side_b') return 'Side B';
    if (cat === 'audio_side_c') return 'Side C';
    if (cat === 'audio_side_d') return 'Side D';
    if (!cat.startsWith('master_slot_')) return null;
    const tail = cat.slice('master_slot_'.length);
    const slotId = tail.includes('_layer') ? tail.split('_layer')[0] : tail;
    const idx = (order?.master_slots || []).findIndex((s) => s.id === slotId);
    if (idx === -1) return null;
    let label = `Disc ${idx + 1}`;
    if (tail.includes('_layer1')) label += ' · Layer 1';
    else if (tail.includes('_layer0')) label += ' · Layer 0';
    return label;
};

function RejectedFileRow({ file, order, onReplaced }) {
    const [pickerOpen, setPickerOpen] = useState(false);
    const discLabel = labelForFile(file, order);

    const handleUploadComplete = async (newFileId) => {
        // After the chunked upload completes, atomically supersede the
        // rejected file. The backend pushes the history entry + recomputes
        // the order-level `needs_master_revision` flag.
        try {
            await ordersApi.markMasterReplaced(order.id, file.id, newFileId);
            toast.success(`Replacement for ${file.original_filename} sent for review.`);
            setPickerOpen(false);
            onReplaced?.();
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Replacement uploaded but failed to link to the rejected file.');
        }
    };

    return (
        <div
            className="border border-rose-300 bg-white rounded-lg p-3 space-y-2"
            data-testid={`customer-rejected-file-${file.id}`}
        >
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                        <XCircle className="h-4 w-4 text-rose-600 flex-shrink-0" />
                        <span className="font-mono text-sm truncate">{file.original_filename}</span>
                        {discLabel && (
                            <span className="px-1.5 py-px text-[10px] rounded bg-slate-100 text-slate-600 border border-slate-200">
                                {discLabel}
                            </span>
                        )}
                    </div>
                    {file.rejection_comment && (
                        <div className="text-sm text-rose-800 bg-rose-50 border border-rose-200 rounded px-3 py-2 whitespace-pre-wrap">
                            <span className="font-semibold">Reason:</span> {file.rejection_comment}
                        </div>
                    )}
                </div>
                {!pickerOpen && (
                    <Button
                        size="sm"
                        className="bg-rose-600 hover:bg-rose-700 flex-shrink-0"
                        onClick={() => setPickerOpen(true)}
                        data-testid={`upload-replacement-btn-${file.id}`}
                    >
                        <Upload className="h-4 w-4 mr-1" />
                        Upload replacement
                    </Button>
                )}
            </div>
            {pickerOpen && (
                <div className="border-t border-rose-200 pt-2">
                    <ChunkedUploader
                        orderId={order.id}
                        category={file.category}
                        label="Choose replacement file"
                        description={`Will be uploaded in place of ${file.original_filename}`}
                        onComplete={(fileResult) => {
                            // `useChunkedUpload` calls onComplete with the
                            // full file record returned by the backend.
                            const newFileId = fileResult?.id;
                            if (newFileId) {
                                handleUploadComplete(newFileId);
                            } else {
                                toast.error('Replacement uploaded but linking failed (no file id returned).');
                            }
                        }}
                    />
                    <div className="flex justify-end mt-2">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setPickerOpen(false)}
                        >
                            Cancel
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}

export function CustomerMasterRejectionBanner({ order, files, onReplaced }) {
    const rejectedFiles = useMemo(() => {
        return (files || []).filter(
            (f) => f.rejected && !f.superseded && f.replacement_required,
        );
    }, [files]);

    if (rejectedFiles.length === 0) return null;

    return (
        <Card className="border-rose-300 bg-rose-50/30 mb-6" data-testid="customer-master-rejection-banner">
            <CardHeader>
                <CardTitle className="flex items-center gap-2 text-rose-700 text-lg">
                    <XCircle className="h-5 w-5" />
                    {rejectedFiles.length === 1
                        ? 'Master file replacement required'
                        : `${rejectedFiles.length} master files need replacement`}
                </CardTitle>
                <CardDescription>
                    The files below were rejected during review. Please upload a replacement
                    for each so we can continue production.
                </CardDescription>
            </CardHeader>
            <CardContent className="space-y-3">
                {rejectedFiles.map((file) => (
                    <RejectedFileRow
                        key={file.id}
                        file={file}
                        order={order}
                        onReplaced={onReplaced}
                    />
                ))}
            </CardContent>
        </Card>
    );
}

export default CustomerMasterRejectionBanner;
